import { __decorate, __param } from '../../../../../../node_modules/tslib/tslib.es6.js';
import * as dom from 'monaco-editor/esm/vs/base/browser/dom.js';
import { CancellationToken } from 'monaco-editor/esm/vs/base/common/cancellation.js';
import { Emitter } from 'monaco-editor/esm/vs/base/common/event.js';
import { Disposable, DisposableStore } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import './media/interactiveSession.css.js';
import { localize } from 'monaco-editor/esm/vs/nls.js';
import { MenuId } from 'monaco-editor/esm/vs/platform/actions/common/actions.js';
import { IContextKeyService } from 'monaco-editor/esm/vs/platform/contextkey/common/contextkey.js';
import { IContextMenuService } from 'monaco-editor/esm/vs/platform/contextview/browser/contextView.js';
import { createDecorator, IInstantiationService } from 'monaco-editor/esm/vs/platform/instantiation/common/instantiation.js';
import { ServiceCollection } from 'monaco-editor/esm/vs/platform/instantiation/common/serviceCollection.js';
import { WorkbenchObjectTree } from 'monaco-editor/esm/vs/platform/list/browser/listService.js';
import { foreground } from 'monaco-editor/esm/vs/platform/theme/common/colorRegistry.js';
import { InteractiveSessionInputPart } from './interactiveSessionInputPart.js';
import { InteractiveSessionListDelegate, InteractiveListItemRenderer, InteractiveSessionAccessibilityProvider } from './interactiveSessionListRenderer.js';
import { InteractiveSessionEditorOptions } from './interactiveSessionOptions.js';
import { CONTEXT_IN_INTERACTIVE_SESSION, CONTEXT_INTERACTIVE_REQUEST_IN_PROGRESS } from '../common/interactiveSessionContextKeys.js';
import { IInteractiveSessionService } from '../common/interactiveSessionService.js';
import { isRequestVM, isWelcomeVM, isResponseVM, InteractiveSessionViewModel } from '../common/interactiveSessionViewModel.js';
const IInteractiveSessionWidgetService = ( createDecorator('interactiveSessionWidgetService'));
const $ = dom.$;
function revealLastElement(list) {
    list.scrollTop = list.scrollHeight - list.renderHeight;
}
let InteractiveSessionWidget = class InteractiveSessionWidget extends Disposable {
    set viewModel(viewModel) {
        if (this._viewModel === viewModel) {
            return;
        }
        this.viewModelDisposables.clear();
        this._viewModel = viewModel;
        if (viewModel) {
            this.viewModelDisposables.add(viewModel);
        }
        this.slashCommandsPromise = undefined;
        this.lastSlashCommands = undefined;
        this.getSlashCommands().then(() => {
            this.onDidChangeItems();
        });
        this._onDidChangeViewModel.fire();
    }
    get viewModel() {
        return this._viewModel;
    }
    constructor(viewContext, listBackgroundColorDelegate, inputEditorBackgroundColorDelegate, resultEditorBackgroundColorDelegate, contextKeyService, instantiationService, interactiveSessionService, interactiveSessionWidgetService, contextMenuService) {
        super();
        this.viewContext = viewContext;
        this.listBackgroundColorDelegate = listBackgroundColorDelegate;
        this.inputEditorBackgroundColorDelegate = inputEditorBackgroundColorDelegate;
        this.resultEditorBackgroundColorDelegate = resultEditorBackgroundColorDelegate;
        this.contextKeyService = contextKeyService;
        this.instantiationService = instantiationService;
        this.interactiveSessionService = interactiveSessionService;
        this.contextMenuService = contextMenuService;
        this._onDidFocus = this._register(( new Emitter()));
        this.onDidFocus = this._onDidFocus.event;
        this._onDidChangeViewModel = this._register(( new Emitter()));
        this.onDidChangeViewModel = this._onDidChangeViewModel.event;
        this.visible = false;
        this.visibleChangeCount = 0;
        this.previousTreeScrollHeight = 0;
        this.viewModelDisposables = ( new DisposableStore());
        CONTEXT_IN_INTERACTIVE_SESSION.bindTo(contextKeyService).set(true);
        this.requestInProgress = CONTEXT_INTERACTIVE_REQUEST_IN_PROGRESS.bindTo(contextKeyService);
        this._register(interactiveSessionWidgetService.register(this));
    }
    get providerId() {
        return this.viewModel?.providerId || '';
    }
    get inputEditor() {
        return this.inputPart.inputEditor;
    }
    get inputUri() {
        return this.inputPart.inputUri;
    }
    render(parent) {
        this.container = dom.append(parent, $('.interactive-session'));
        this.listContainer = dom.append(this.container, $(`.interactive-list`));
        const viewId = 'viewId' in this.viewContext ? this.viewContext.viewId : undefined;
        this.editorOptions = this._register(this.instantiationService.createInstance(InteractiveSessionEditorOptions, viewId, this.inputEditorBackgroundColorDelegate, this.resultEditorBackgroundColorDelegate));
        this.createList(this.listContainer);
        this.createInput(this.container);
        this._register(this.editorOptions.onDidChange(() => this.onDidStyleChange()));
        this.onDidStyleChange();
        if (this.viewModel) {
            this.onDidChangeItems();
            revealLastElement(this.tree);
        }
        InteractiveSessionWidget.CONTRIBS.forEach(contrib => this._register(this.instantiationService.createInstance(contrib, this)));
    }
    focusInput() {
        this.inputPart.focus();
    }
    onDidChangeItems() {
        if (this.tree && this.visible) {
            const treeItems = ( (this.viewModel?.getItems() ?? [])
                .map(item => {
                return {
                    element: item,
                    collapsed: false,
                    collapsible: false
                };
            }));
            this.tree.setChildren(null, treeItems, {
                diffIdentityProvider: {
                    getId: (element) => {
                        return element.id +
                            `${(isRequestVM(element) || isWelcomeVM(element)) && !!this.lastSlashCommands ? '_scLoaded' : ''}` +
                            `${isResponseVM(element) && element.renderData ? `_${this.visibleChangeCount}` : ''}`;
                    },
                }
            });
            const lastItem = treeItems[treeItems.length - 1]?.element;
            if (lastItem && isResponseVM(lastItem) && lastItem.isComplete) {
                this.renderFollowups(lastItem.replyFollowups);
            }
            else {
                this.renderFollowups(undefined);
            }
        }
    }
    async renderFollowups(items) {
        this.inputPart.renderFollowups(items);
        if (this.bodyDimension) {
            this.layout(this.bodyDimension.height, this.bodyDimension.width);
        }
    }
    setVisible(visible) {
        this.visible = visible;
        this.visibleChangeCount++;
        this.renderer.setVisible(visible);
        if (visible) {
            this.onDidChangeItems();
        }
    }
    async getSlashCommands() {
        if (!this.viewModel) {
            return;
        }
        if (!this.slashCommandsPromise) {
            this.slashCommandsPromise = this.interactiveSessionService.getSlashCommands(this.viewModel.sessionId, CancellationToken.None).then(commands => {
                const clearCommand = {
                    command: 'clear',
                    sortText: 'z_clear',
                    detail: ( localize('clear', "Clear the session")),
                };
                this.lastSlashCommands = [
                    ...(commands ?? []),
                    clearCommand
                ];
                return this.lastSlashCommands;
            });
        }
        return this.slashCommandsPromise;
    }
    createList(listContainer) {
        const scopedInstantiationService = this.instantiationService.createChild(( new ServiceCollection([IContextKeyService, this.contextKeyService])));
        const delegate = scopedInstantiationService.createInstance(InteractiveSessionListDelegate);
        const rendererDelegate = {
            getListLength: () => this.tree.getNode(null).visibleChildrenCount,
            getSlashCommands: () => this.lastSlashCommands ?? [],
        };
        this.renderer = this._register(scopedInstantiationService.createInstance(InteractiveListItemRenderer, this.editorOptions, rendererDelegate));
        this._register(this.renderer.onDidClickFollowup(item => {
            this.acceptInput(item);
        }));
        this.tree = scopedInstantiationService.createInstance(WorkbenchObjectTree, 'InteractiveSession', listContainer, delegate, [this.renderer], {
            identityProvider: { getId: (e) => e.id },
            supportDynamicHeights: true,
            hideTwistiesOfChildlessElements: true,
            accessibilityProvider: ( new InteractiveSessionAccessibilityProvider()),
            keyboardNavigationLabelProvider: { getKeyboardNavigationLabel: (e) => isRequestVM(e) ? e.message : isResponseVM(e) ? e.response.value : '' },
            setRowLineHeight: false,
            overrideStyles: {
                listFocusBackground: this.listBackgroundColorDelegate(),
                listInactiveFocusBackground: this.listBackgroundColorDelegate(),
                listActiveSelectionBackground: this.listBackgroundColorDelegate(),
                listFocusAndSelectionBackground: this.listBackgroundColorDelegate(),
                listInactiveSelectionBackground: this.listBackgroundColorDelegate(),
                listHoverBackground: this.listBackgroundColorDelegate(),
                listBackground: this.listBackgroundColorDelegate(),
                listFocusForeground: foreground,
                listHoverForeground: foreground,
                listInactiveFocusForeground: foreground,
                listInactiveSelectionForeground: foreground,
                listActiveSelectionForeground: foreground,
                listFocusAndSelectionForeground: foreground,
            }
        });
        this.tree.onContextMenu(e => this.onContextMenu(e));
        this._register(this.tree.onDidChangeContentHeight(() => {
            this.onDidChangeTreeContentHeight();
        }));
        this._register(this.renderer.onDidChangeItemHeight(e => {
            this.tree.updateElementHeight(e.element, e.height);
        }));
        this._register(this.tree.onDidFocus(() => {
            this._onDidFocus.fire();
        }));
    }
    onContextMenu(e) {
        e.browserEvent.preventDefault();
        e.browserEvent.stopPropagation();
        this.contextMenuService.showContextMenu({
            menuId: MenuId.InteractiveSessionContext,
            menuActionOptions: { shouldForwardArgs: true },
            contextKeyService: this.contextKeyService,
            getAnchor: () => e.anchor,
            getActionsContext: () => e.element,
        });
    }
    onDidChangeTreeContentHeight() {
        if (this.tree.scrollHeight !== this.previousTreeScrollHeight) {
            const lastElementWasVisible = this.tree.scrollTop + this.tree.renderHeight >= this.previousTreeScrollHeight - 2;
            if (lastElementWasVisible) {
                dom.scheduleAtNextAnimationFrame(() => {
                    revealLastElement(this.tree);
                }, 0);
            }
        }
        this.previousTreeScrollHeight = this.tree.scrollHeight;
    }
    createInput(container) {
        this.inputPart = this.instantiationService.createInstance(InteractiveSessionInputPart);
        this.inputPart.render(container, '', this);
        this._register(this.inputPart.onDidFocus(() => this._onDidFocus.fire()));
        this._register(this.inputPart.onDidAcceptFollowup(followup => this.acceptInput(followup)));
        this._register(this.inputPart.onDidChangeHeight(() => this.bodyDimension && this.layout(this.bodyDimension.height, this.bodyDimension.width)));
    }
    onDidStyleChange() {
        this.container.style.setProperty('--vscode-interactive-result-editor-background-color', this.editorOptions.configuration.resultEditor.backgroundColor?.toString() ?? '');
    }
    setModel(model, viewState) {
        if (!this.container) {
            throw new Error('Call render() before setModel()');
        }
        this.viewModel = this.instantiationService.createInstance(InteractiveSessionViewModel, model);
        this.viewModelDisposables.add(this.viewModel.onDidChange(() => {
            this.slashCommandsPromise = undefined;
            this.requestInProgress.set(this.viewModel.requestInProgress);
            this.onDidChangeItems();
        }));
        this.viewModelDisposables.add(this.viewModel.onDidDisposeModel(() => {
            this.viewModel = undefined;
            this.onDidChangeItems();
        }));
        this.inputPart.setState(model.providerId, viewState.inputValue ?? '');
        if (this.tree) {
            this.onDidChangeItems();
            revealLastElement(this.tree);
        }
    }
    async acceptInput(query) {
        if (this.viewModel) {
            const editorValue = this.inputPart.inputEditor.getValue();
            if (!query && editorValue.trim() === '/clear') {
                this.interactiveSessionService.clearSession(this.viewModel.sessionId);
                this.inputPart.inputEditor.setValue('');
                return;
            }
            const input = query ?? editorValue;
            const result = await this.interactiveSessionService.sendRequest(this.viewModel.sessionId, input);
            if (result) {
                revealLastElement(this.tree);
                this.inputPart.acceptInput(query);
            }
        }
    }
    focusLastMessage() {
        if (!this.viewModel) {
            return;
        }
        const items = this.tree.getNode(null).children;
        const lastItem = items[items.length - 1];
        if (!lastItem) {
            return;
        }
        this.tree.setFocus([lastItem.element]);
        this.tree.domFocus();
    }
    layout(height, width) {
        this.bodyDimension = new dom.Dimension(width, height);
        const inputPartHeight = this.inputPart.layout(height, width);
        const lastElementVisible = this.tree.scrollTop + this.tree.renderHeight >= this.tree.scrollHeight;
        const listHeight = height - inputPartHeight;
        this.tree.layout(listHeight, width);
        this.tree.getHTMLElement().style.height = `${listHeight}px`;
        this.renderer.layout(width);
        if (lastElementVisible) {
            revealLastElement(this.tree);
        }
        this.listContainer.style.height = `${height - inputPartHeight}px`;
    }
    saveState() {
        this.inputPart.saveState();
    }
    getViewState() {
        this.inputPart.saveState();
        return { inputValue: this.inputPart.inputEditor.getValue() };
    }
};
InteractiveSessionWidget.CONTRIBS = [];
InteractiveSessionWidget = ( __decorate([
    ( __param(4, IContextKeyService)),
    ( __param(5, IInstantiationService)),
    ( __param(6, IInteractiveSessionService)),
    ( __param(7, IInteractiveSessionWidgetService)),
    ( __param(8, IContextMenuService))
], InteractiveSessionWidget));
export { IInteractiveSessionWidgetService, InteractiveSessionWidget };
